import { useState } from "react";
import { Button } from "@/lib/components/Button/Button";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { cn } from "@/lib/utils";
import {
  IconAlertTriangle,
  IconCopy,
  IconDots,
  IconDownload,
  IconLink,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import ConfirmDeleteDialog from "@/lib/components/ConfirmDeleteDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";

export type ShareLinkStatus = "generating" | "done" | "failed";

type Props = {
  status: ShareLinkStatus;
  link: string | null;
  isRendering: boolean;
  onCopy: () => void;
  onDownload: () => void;
  onRemove: () => void;
  onRetry?: () => void;
  isRemoving: boolean;
  isRetrying: boolean;
};

export function ShareLinkCard({
  status,
  link,
  isRendering,
  onCopy,
  onDownload,
  onRemove,
  onRetry,
  isRemoving,
  isRetrying,
}: Props) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleRemovePublicLink = () => {
    onRemove();
  };

  return (
    <div className="flex w-full justify-center px-8 pt-8">
      <div className="flex w-full max-w-115 flex-col items-center md:w-115">
        <div className="relative flex w-full flex-row items-center gap-4 overflow-hidden rounded-lg border border-border px-4 py-2.5">
          <div className="flex h-10 w-10 items-center justify-center">
            {status === "generating" && <LoadingSpinner size={20} />}
            {status === "done" && <IconLink size={20} />}
            {status === "failed" && (
              <IconAlertTriangle size={20} className="text-error" />
            )}
          </div>
          <div
            className={cn(
              "flex flex-1 flex-col overflow-hidden",
              status !== "done" && "flex-col-reverse",
            )}
          >
            <p className="line-clamp-1 truncate text-xs text-text-secondary">
              {status === "generating" && "This may take a moment"}
              {status === "failed" &&
                "Something went wrong while creating the link"}
              {status === "done" && "Public link"}
            </p>
            <p className="text-sm font-medium">
              {status === "generating" &&
                (isRendering ? "Rendering video…" : "Generating link...")}
              {status === "failed" && "Failed to create link"}
              {status === "done" && link && (
                <span className="relative block">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block overflow-x-auto pr-3 whitespace-nowrap"
                  >
                    {link}
                  </a>
                  <span className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-r from-transparent to-background" />
                </span>
              )}
            </p>
          </div>

          {status === "failed" && onRetry && (
            <Button
              variant="outline"
              size="iconSm"
              onClick={onRetry}
              isLoading={isRetrying}
            >
              <IconRefresh />
            </Button>
          )}

          {status === "done" && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="iconSm"
                className="[&_svg]:size-5"
                onClick={onCopy}
              >
                <IconCopy />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="[&_svg]:size-5"
                  >
                    <IconDots />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onDownload}>
                    <IconDownload />
                    Download
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-error focus:text-error"
                    onClick={() => setIsConfirmOpen(true)}
                  >
                    <IconTrash />
                    Remove public link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        {status === "done" && (
          <ConfirmDeleteDialog
            open={isConfirmOpen}
            onOpenChange={setIsConfirmOpen}
            title="Remove public link"
            description="Anyone with this link will no longer be able to view the video."
            confirmLabel="Remove link"
            isDeleting={isRemoving}
            onConfirm={handleRemovePublicLink}
          />
        )}
      </div>
    </div>
  );
}
